/**
 * Ticket Notification Utilities & Audio Synthesizer
 * 
 * Provides:
 * 1. Web Audio API synthesized chime (zero external assets needed, 100% reliable)
 * 2. Browser Desktop Notification API integration
 * 3. User interaction audio unlocker for mobile/desktop browsers
 */

let sharedAudioContext: AudioContext | null = null;
let isAudioUnlocked = false;

export function getSharedAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!sharedAudioContext) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      sharedAudioContext = new AudioContextClass();
    }
  }
  return sharedAudioContext;
}

export function unlockAudioContext() {
  if (isAudioUnlocked) return;
  const ctx = getSharedAudioContext();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().then(() => {
      isAudioUnlocked = true;
      console.log('[Audio] Notification AudioContext unlocked successfully');
    }).catch(err => {
      console.warn('[Audio] Failed to unlock AudioContext:', err);
    });
  } else if (ctx && ctx.state === 'running') {
    isAudioUnlocked = true;
  }
}

// Auto-register unlock listeners on first user gesture
if (typeof window !== 'undefined') {
  const handleUserGesture = () => {
    unlockAudioContext();
    window.removeEventListener('click', handleUserGesture);
    window.removeEventListener('touchstart', handleUserGesture);
    window.removeEventListener('keydown', handleUserGesture);
  };
  window.addEventListener('click', handleUserGesture, { passive: true });
  window.addEventListener('touchstart', handleUserGesture, { passive: true });
  window.addEventListener('keydown', handleUserGesture, { passive: true });
}

/**
 * Plays an upbeat, pleasant tri-tone notification chime (C5 -> E5 -> G5)
 */
export function playTicketChime() {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    const executeChime = () => {
      const playTone = (freq: number, startTime: number, duration: number, volume: number) => {
        try {
          const osc = ctx.createOscillator();
          const gainNode = ctx.createGain();
          osc.connect(gainNode);
          gainNode.connect(ctx.destination);

          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, startTime);

          gainNode.gain.setValueAtTime(0, startTime);
          gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.04);
          gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

          osc.start(startTime);
          osc.stop(startTime + duration);
        } catch (e) {
          console.warn('[Audio] playTone error:', e);
        }
      };

      const now = ctx.currentTime;
      playTone(523.25, now, 0.35, 0.45);        // C5
      playTone(659.25, now + 0.14, 0.35, 0.45);  // E5
      playTone(783.99, now + 0.28, 0.60, 0.55);  // G5
    };

    if (ctx.state === 'suspended') {
      ctx.resume().then(executeChime).catch(executeChime);
    } else {
      executeChime();
    }
  } catch (err) {
    console.warn('[Audio] Failed to play ticket chime:', err);
  }
}

/**
 * Plays a distinct, urgent double alarm chime for high priority & waiting for response tickets
 */
export function playUrgentTicketChime() {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    const executeUrgent = () => {
      const playTone = (freq: number, startTime: number, duration: number, volume: number) => {
        try {
          const osc = ctx.createOscillator();
          const gainNode = ctx.createGain();
          osc.connect(gainNode);
          gainNode.connect(ctx.destination);

          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, startTime);

          gainNode.gain.setValueAtTime(0, startTime);
          gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
          gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

          osc.start(startTime);
          osc.stop(startTime + duration);
        } catch (e) {}
      };

      const now = ctx.currentTime;
      playTone(880, now, 0.14, 0.55);            // A5
      playTone(1046.50, now + 0.14, 0.22, 0.65);  // C6
      playTone(880, now + 0.40, 0.14, 0.55);      // A5
      playTone(1174.66, now + 0.54, 0.32, 0.70);  // D6
    };

    if (ctx.state === 'suspended') {
      ctx.resume().then(executeUrgent).catch(executeUrgent);
    } else {
      executeUrgent();
    }
  } catch (err) {
    console.warn('[Audio] Failed to play urgent chime:', err);
  }
}

/**
 * Request browser native notification permission
 */
export async function requestDesktopNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false;
  }
  try {
    if (Notification.permission === 'granted') return true;
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  } catch (err) {
    console.warn('[DesktopNotification] Permission request failed:', err);
    return false;
  }
}

/**
 * Show a native OS/Browser notification (Cross-platform support for Desktop & Mobile PWA)
 */
export async function showDesktopNotification(title: string, options?: { body?: string; icon?: string; tag?: string; onClick?: () => void }) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  const notifOptions: any = {
    body: options?.body || 'توجد تذكرة جديدة بانتظار استجابتك في النظام',
    icon: options?.icon || '/logo.png',
    badge: '/logo.png',
    tag: options?.tag || `ftth-ticket-alert-${Date.now()}`,
    dir: 'rtl',
    vibrate: [200, 100, 200],
    data: { url: window.location.origin + '/#/tickets' }
  };

  // 1. Try Service Worker showNotification first (Works on Mobile Chrome, PWA, Android)
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(title, notifOptions);
        return;
      }
    } catch (swErr) {
      console.warn('[DesktopNotification] ServiceWorker showNotification notice:', swErr);
    }
  }

  // 2. Fallback to standard Notification constructor (Desktop Chrome/Firefox/Safari)
  try {
    const notif = new Notification(title, notifOptions);
    notif.onclick = () => {
      window.focus();
      if (options?.onClick) {
        options.onClick();
      } else {
        window.location.hash = '#/tickets';
      }
      notif.close();
    };
  } catch (err) {
    console.warn('[DesktopNotification] Standard Notification notice:', err);
  }
}

